import { errorWords } from "@alm/domain";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { optionalString } from "@/lib/search";
import type { Permission } from "@alm/policy";
import { EmptyState } from "@/components/app/lists/empty-state";
import { ReadFailed } from "@/components/app/lists/read-failed";
import { Skeleton } from "@/components/ui/skeleton";
import { orpc } from "@/lib/orpc";
import { useCanOrganizationWide } from "@/lib/policy";
import { RoleEditor } from "./-components/role-editor";
import { useStartingPoints } from "./-components/start-from-picker";

/** `?from=med-tech`: the job the page opens with. */
const searchSchema = (search: Record<string, unknown>): { from?: string } => ({
  from: optionalString(search.from),
});

export const Route = createFileRoute("/_authenticated/_administration/settings/roles/new")({
  validateSearch: searchSchema,
  staticData: { fullWidth: true },
  component: NewRolePage,
});

/** A role from nothing, or from a job the picker names. */
function NewRolePage() {
  const { from } = Route.useSearch();
  const points = useStartingPoints();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const grantable = useQuery(orpc.administration.roles.permissions.queryOptions({}));
  const canEdit = useCanOrganizationWide("organization:manage-roles");

  const create = useMutation(
    orpc.administration.roles.create.mutationOptions({
      onSuccess: async (role) => {
        toast.success(`${role.name} created`);
        await queryClient.invalidateQueries({ queryKey: orpc.administration.roles.key() });
        await navigate({ to: "/settings/roles/$roleId", params: { roleId: role.id } });
      },
      onError: (error: Error) => toast.error(errorWords(error)),
    }),
  );

  if (!canEdit) {
    return (
      <EmptyState
        title="Roles are the organization's to change"
        description="A role reaches everybody holding it at every building, so only someone who manages roles for the whole organization can add one."
      />
    );
  }
  if (grantable.isPending || (from && points.length === 0))
    return <Skeleton className="h-96 w-full" />;
  if (grantable.isError) {
    return (
      <ReadFailed
        message="The permissions could not be loaded."
        error={grantable.error}
        retrying={grantable.isFetching}
        onRetry={() => void grantable.refetch()}
      />
    );
  }

  const offered = grantable.data as Permission[];
  const point = points.find((candidate) => candidate.id === from);

  return (
    <RoleEditor
      key={from ?? "blank"}
      title="New role"
      initial={{
        name: point ? point.name : "",
        description: point?.description ?? "",
        permissions: point ? point.permissions.filter((p) => offered.includes(p)) : [],
      }}
      grantable={offered}
      saveUnchanged
      pending={create.isPending}
      onSave={(values) => create.mutate(values)}
    />
  );
}
